import { useEffect, useMemo, useState } from 'react';
import {
  ArrowLeft,
  ChevronRight,
  Eye,
  EyeOff,
  ImagePlus,
  Lock,
  ShieldCheck,
  UserCircle2,
  UserRound,
  X,
} from 'lucide-react';
import { ModalBackdrop } from '../common';
import {
  changePortalPassword,
  resolvePortalAssetUrl,
  updatePortalProfile,
} from '../../services/eportalAuthService';

const settingsSections = [
  {
    key: 'profile',
    title: 'Profile',
    description: 'Update your account details and display image.',
    icon: UserRound,
  },
  {
    key: 'security',
    title: 'Account Security',
    description: 'Manage password and sign-in protection.',
    icon: Lock,
  },
];

function ProfileSettingsModal({
  isOpen,
  forcePasswordChange = false,
  onClose,
  onUserChange,
  user,
}) {
  const [activeSection, setActiveSection] = useState('menu');
  const [accountName, setAccountName] = useState('');
  const [avatarPreview, setAvatarPreview] = useState('');
  const [avatarFile, setAvatarFile] = useState(null);
  const [profileError, setProfileError] = useState('');
  const [profileMessage, setProfileMessage] = useState('');
  const [isProfileSubmitting, setIsProfileSubmitting] = useState(false);
  const [passwordValues, setPasswordValues] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [passwordVisibility, setPasswordVisibility] = useState({
    currentPassword: false,
    newPassword: false,
    confirmPassword: false,
  });
  const [passwordError, setPasswordError] = useState('');
  const [passwordMessage, setPasswordMessage] = useState('');
  const [isPasswordSubmitting, setIsPasswordSubmitting] = useState(false);
  const resolvedAvatar = useMemo(() => resolvePortalAssetUrl(user?.avatar), [user?.avatar]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    setActiveSection(forcePasswordChange ? 'security' : 'menu');
    setAccountName(user?.username || '');
    setAvatarPreview(resolvedAvatar);
    setAvatarFile(null);
    setProfileError('');
    setProfileMessage('');
    setPasswordError('');
    setPasswordMessage('');
    setPasswordValues({
      currentPassword: '',
      newPassword: '',
      confirmPassword: '',
    });
    setPasswordVisibility({
      currentPassword: false,
      newPassword: false,
      confirmPassword: false,
    });
  }, [forcePasswordChange, isOpen, resolvedAvatar, user?.username]);

  if (!isOpen) {
    return null;
  }

  function handleAvatarChange(event) {
    const selectedFile = event.target.files?.[0];

    if (!selectedFile) {
      return;
    }

    if (!selectedFile.type.startsWith('image/')) {
      setProfileError('Please choose an image file only.');
      return;
    }

    if (selectedFile.size > 2 * 1024 * 1024) {
      setProfileError('Profile image must be 2 MB or smaller.');
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      setAvatarFile(selectedFile);
      setAvatarPreview(typeof reader.result === 'string' ? reader.result : '');
      setProfileError('');
      setProfileMessage('');
    };
    reader.readAsDataURL(selectedFile);
  }

  function handleRemoveAvatar() {
    setAvatarFile(null);
    setAvatarPreview('');
    setProfileError('');
    setProfileMessage('');
  }

  async function handleProfileSubmit(event) {
    event.preventDefault();

    const nextName = accountName.trim();

    if (!nextName) {
      setProfileError('Username is required.');
      return;
    }

    setIsProfileSubmitting(true);
    setProfileError('');
    setProfileMessage('');

    try {
      const payload = new FormData();
      payload.append('username', nextName);

      if (avatarFile) {
        payload.append('avatar', avatarFile);
      } else if (!avatarPreview && user?.avatar) {
        payload.append('remove_avatar', 'true');
      }

      const response = await updatePortalProfile(payload);
      onUserChange(response.user);
      setAvatarFile(null);
      setProfileMessage(response.message || 'Profile updated successfully.');
    } catch (error) {
      setProfileError(error.message);
    } finally {
      setIsProfileSubmitting(false);
    }
  }

  function handlePasswordChange(event) {
    const { name, value } = event.target;

    setPasswordValues((currentValue) => ({
      ...currentValue,
      [name]: value,
    }));
    setPasswordError('');
    setPasswordMessage('');
  }

  function togglePasswordVisibility(name) {
    setPasswordVisibility((currentValue) => ({
      ...currentValue,
      [name]: !currentValue[name],
    }));
  }

  async function handlePasswordSubmit(event) {
    event.preventDefault();

    if (
      !passwordValues.currentPassword ||
      !passwordValues.newPassword ||
      !passwordValues.confirmPassword
    ) {
      setPasswordError('Complete all password fields first.');
      return;
    }

    setIsPasswordSubmitting(true);
    setPasswordError('');
    setPasswordMessage('');

    try {
      const response = await changePortalPassword({
        current_password: passwordValues.currentPassword,
        new_password: passwordValues.newPassword,
        confirm_password: passwordValues.confirmPassword,
      });

      onUserChange(response.user, {
        passwordChanged: true,
        message: response.message || 'Password updated successfully.',
      });
      setPasswordValues({
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
      });
      setPasswordMessage(response.message || 'Password updated successfully.');
      if (forcePasswordChange) {
        onClose();
      }
    } catch (error) {
      setPasswordError(error.message);
    } finally {
      setIsPasswordSubmitting(false);
    }
  }

  return (
    <ModalBackdrop backdropClassName="bg-[rgba(17,17,17,0.26)] backdrop-blur-[4px]">
      <div className="w-full max-w-[780px] overflow-hidden rounded-[34px] border border-[var(--theme-border)] bg-[linear-gradient(180deg,#ffffff_0%,var(--theme-surface-soft)_100%)] shadow-[0_28px_70px_rgba(var(--theme-shadow-rgb),0.2)]">
        <div className="flex items-start justify-between gap-4 border-b border-[var(--theme-border-soft)] px-5 py-5 sm:px-7 sm:py-6">
          <div className="min-w-0">
            <span className="inline-flex rounded-full border border-[var(--theme-border)] bg-white px-4 py-2 text-[0.82rem] font-semibold uppercase tracking-[0.26em] text-[var(--theme-accent)]">
              Workspace Controls
            </span>
            <h2 className="m-0 mt-5 text-[1.9rem] font-semibold tracking-[-0.03em] text-[var(--theme-text-strong)]">
              Settings
            </h2>
            <p className="m-0 mt-2 text-[1.02rem] text-[var(--theme-text-muted)]">
              {forcePasswordChange
                ? 'Change your temporary password before continuing.'
                : 'Refine profile and security preferences from one place.'}
            </p>
          </div>
          <div className="flex items-start gap-3">
            <div className="flex h-[74px] w-[74px] items-center justify-center rounded-[24px] border border-[var(--theme-border-soft)] bg-white shadow-[0_12px_28px_rgba(var(--theme-shadow-rgb),0.08)]">
              <div className="flex h-[60px] w-[60px] items-center justify-center rounded-[20px] bg-[var(--theme-accent)] text-white">
                <ShieldCheck className="h-7 w-7" />
              </div>
            </div>
            {!forcePasswordChange ? (
              <button
                aria-label="Close settings"
                className="inline-flex h-12 w-12 items-center justify-center rounded-full border border-[var(--theme-border)] bg-white text-[var(--theme-text-muted)] shadow-[0_12px_28px_rgba(var(--theme-shadow-rgb),0.08)] transition hover:bg-[var(--theme-surface-soft)]"
                onClick={onClose}
                type="button"
              >
                <X className="h-5 w-5" />
              </button>
            ) : null}
          </div>
        </div>

        <div className="px-5 py-5 sm:px-7 sm:py-6">
          {user?.mustChangePassword ? (
            <div className="mb-5 flex items-start gap-3 rounded-[24px] border border-[var(--theme-accent)] bg-[var(--theme-accent-soft)] px-5 py-4 text-[var(--theme-accent-strong)]">
              <ShieldCheck className="mt-0.5 h-5 w-5 shrink-0" />
              <strong className="text-[0.98rem] font-semibold">
                You are using a temporary password. Update it now to continue.
              </strong>
            </div>
          ) : null}

          {activeSection === 'menu' ? (
            <div className="grid gap-4">
              <div className="rounded-[28px] border border-[var(--theme-border-soft)] bg-white px-6 py-5 shadow-[0_8px_20px_rgba(var(--theme-shadow-rgb),0.05)]">
                <h3 className="m-0 text-[1rem] font-semibold text-[var(--theme-text-strong)]">
                  Choose a settings area
                </h3>
                <p className="m-0 mt-2 text-[0.98rem] text-[var(--theme-text-muted)]">
                  Each section opens in a cleaner workspace so updates feel easier to manage.
                </p>
              </div>

              {settingsSections.map((section) => {
                const Icon = section.icon;

                return (
                  <button
                    key={section.key}
                    className="flex w-full items-center gap-4 rounded-[26px] border border-[var(--theme-border)] bg-white px-5 py-4 text-left shadow-[0_8px_20px_rgba(var(--theme-shadow-rgb),0.05)] transition hover:-translate-y-0.5 hover:border-[var(--theme-accent)] hover:shadow-[0_12px_24px_rgba(var(--theme-shadow-rgb),0.1)]"
                    onClick={() => setActiveSection(section.key)}
                    type="button"
                  >
                    <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-[20px] bg-[var(--theme-accent-soft)] text-[var(--theme-accent-strong)]">
                      <Icon className="h-6 w-6" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="text-[1.05rem] font-semibold text-[var(--theme-text-strong)]">
                        {section.title}
                      </div>
                      <div className="mt-1 text-[0.98rem] text-[var(--theme-text-muted)]">
                        {section.description}
                      </div>
                    </div>
                    <ChevronRight className="h-5 w-5 shrink-0 text-[var(--theme-text-muted)]" />
                  </button>
                );
              })}
            </div>
          ) : null}

          {activeSection === 'profile' ? (
            <form className="grid gap-5" onSubmit={handleProfileSubmit}>
              {!forcePasswordChange ? (
                <button
                  className="inline-flex w-fit items-center gap-2 rounded-full border border-[var(--theme-border)] bg-white px-4 py-2 text-[0.9rem] font-semibold text-[var(--theme-text-subtle)] transition hover:bg-[var(--theme-surface-soft)]"
                  onClick={() => setActiveSection('menu')}
                  type="button"
                >
                  <ArrowLeft className="h-4 w-4" />
                  Back
                </button>
              ) : null}

              <div className="rounded-[28px] border border-[var(--theme-border)] bg-white p-5 shadow-[0_8px_20px_rgba(var(--theme-shadow-rgb),0.05)]">
                <div className="flex flex-col gap-5 sm:flex-row">
                  <div className="flex flex-col items-center gap-3">
                    {avatarPreview ? (
                      <img
                        alt={`${accountName || 'User'} avatar`}
                        className="h-24 w-24 rounded-full border border-[var(--theme-border)] object-cover"
                        src={avatarPreview}
                      />
                    ) : (
                      <div className="flex h-24 w-24 items-center justify-center rounded-full bg-[var(--theme-accent-soft)] text-[var(--theme-accent-strong)]">
                        <UserCircle2 className="h-10 w-10" />
                      </div>
                    )}
                    <label className="inline-flex cursor-pointer items-center gap-2 rounded-full border border-[var(--theme-border)] bg-[var(--theme-surface-soft)] px-4 py-2 text-[0.88rem] font-semibold text-[var(--theme-text-subtle)] transition hover:bg-[var(--theme-accent-soft)]">
                      <ImagePlus className="h-4 w-4" />
                      Upload Photo
                      <input className="hidden" onChange={handleAvatarChange} type="file" accept="image/*" />
                    </label>
                    {avatarPreview ? (
                      <button
                        className="text-[0.85rem] font-semibold text-[#b95a46] transition hover:text-[#9f4736]"
                        onClick={handleRemoveAvatar}
                        type="button"
                      >
                        Remove photo
                      </button>
                    ) : null}
                  </div>

                  <div className="min-w-0 flex-1">
                    <label className="grid gap-2">
                      <span className="text-[0.82rem] font-semibold uppercase tracking-[0.18em] text-[var(--theme-text-muted)]">
                        Username
                      </span>
                      <input
                        className="min-h-[48px] rounded-[18px] border border-[var(--theme-border)] bg-[var(--theme-surface-warm)] px-4 text-[1rem] text-[var(--theme-text-strong)] outline-none transition focus:border-[var(--theme-accent)]"
                        onChange={(event) => {
                          setAccountName(event.target.value);
                          setProfileError('');
                          setProfileMessage('');
                        }}
                        value={accountName}
                      />
                    </label>
                    <p className="m-0 mt-3 text-[0.92rem] text-[var(--theme-text-muted)]">
                      This updates your Seaweeds account label and e-Portal profile name together.
                    </p>
                  </div>
                </div>

                {profileError ? (
                  <p className="m-0 mt-4 rounded-[18px] border border-[#f3c7b9] bg-[#fff4ef] px-4 py-3 text-[0.92rem] text-[#cf5f3f]">
                    {profileError}
                  </p>
                ) : null}
                {profileMessage ? (
                  <p className="m-0 mt-4 rounded-[18px] border border-[#d6ead8] bg-[#f3fbf4] px-4 py-3 text-[0.92rem] text-[#2f7a45]">
                    {profileMessage}
                  </p>
                ) : null}

                <div className="mt-5 flex justify-end">
                  <button
                    className="rounded-full bg-[var(--theme-accent)] px-5 py-2.5 text-[0.92rem] font-semibold text-white transition hover:bg-[var(--theme-accent-strong)] disabled:cursor-not-allowed disabled:bg-[var(--theme-accent-soft)] disabled:text-[var(--theme-accent-strong)]"
                    disabled={isProfileSubmitting}
                    type="submit"
                  >
                    {isProfileSubmitting ? 'Saving...' : 'Save Profile'}
                  </button>
                </div>
              </div>
            </form>
          ) : null}

          {activeSection === 'security' ? (
            <form className="grid gap-5" onSubmit={handlePasswordSubmit}>
              {!forcePasswordChange ? (
                <button
                  className="inline-flex w-fit items-center gap-2 rounded-full border border-[var(--theme-border)] bg-white px-4 py-2 text-[0.9rem] font-semibold text-[var(--theme-text-subtle)] transition hover:bg-[var(--theme-surface-soft)]"
                  onClick={() => setActiveSection('menu')}
                  type="button"
                >
                  <ArrowLeft className="h-4 w-4" />
                  Back
                </button>
              ) : null}

              <div className="grid gap-3 rounded-[28px] border border-[var(--theme-border)] bg-white p-5 shadow-[0_8px_20px_rgba(var(--theme-shadow-rgb),0.05)]">
                <PasswordField
                  description="Current Password"
                  name="currentPassword"
                  onChange={handlePasswordChange}
                  onToggleVisibility={togglePasswordVisibility}
                  placeholder="Enter current password"
                  type="password"
                  value={passwordValues.currentPassword}
                  visible={passwordVisibility.currentPassword}
                />
                <PasswordField
                  description="New Password"
                  name="newPassword"
                  onChange={handlePasswordChange}
                  onToggleVisibility={togglePasswordVisibility}
                  placeholder="Enter new password"
                  type="password"
                  value={passwordValues.newPassword}
                  visible={passwordVisibility.newPassword}
                />
                <PasswordField
                  description="Confirm Password"
                  name="confirmPassword"
                  onChange={handlePasswordChange}
                  onToggleVisibility={togglePasswordVisibility}
                  placeholder="Confirm new password"
                  type="password"
                  value={passwordValues.confirmPassword}
                  visible={passwordVisibility.confirmPassword}
                />

                {passwordError ? (
                  <p className="m-0 rounded-[18px] border border-[#f3c7b9] bg-[#fff4ef] px-4 py-3 text-[0.92rem] text-[#cf5f3f]">
                    {passwordError}
                  </p>
                ) : null}
                {passwordMessage ? (
                  <p className="m-0 rounded-[18px] border border-[#d6ead8] bg-[#f3fbf4] px-4 py-3 text-[0.92rem] text-[#2f7a45]">
                    {passwordMessage}
                  </p>
                ) : null}

                <div className="flex justify-end">
                  <button
                    className="rounded-full bg-[var(--theme-accent)] px-5 py-2.5 text-[0.92rem] font-semibold text-white transition hover:bg-[var(--theme-accent-strong)] disabled:cursor-not-allowed disabled:bg-[var(--theme-accent-soft)] disabled:text-[var(--theme-accent-strong)]"
                    disabled={isPasswordSubmitting}
                    type="submit"
                  >
                    {isPasswordSubmitting ? 'Updating...' : 'Update Password'}
                  </button>
                </div>
              </div>
            </form>
          ) : null}
        </div>
      </div>
    </ModalBackdrop>
  );
}

function PasswordField({
  description,
  name,
  onChange,
  onToggleVisibility,
  placeholder,
  type,
  value,
  visible,
}) {
  return (
    <label className="grid gap-2 rounded-[18px] border border-[var(--theme-border)] bg-white px-3.5 py-3 shadow-[0_8px_20px_rgba(var(--theme-shadow-rgb),0.05)]">
      <div className="flex items-center gap-3">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-[16px] bg-[var(--theme-accent-soft)] text-[var(--theme-accent-strong)]">
          <Lock className="h-4 w-4" />
        </div>
        <div className="min-w-0 flex-1">
          <span className="block text-[11px] font-semibold uppercase tracking-[0.2em] text-[var(--theme-text-muted)]">
            {description}
          </span>
          <div className="relative mt-2">
            <input
              className="w-full border-0 bg-transparent pr-10 text-[15px] text-[var(--theme-text)] outline-none placeholder:text-[var(--theme-text-muted)] sm:text-base"
              name={name}
              onChange={onChange}
              placeholder={placeholder}
              type={visible ? 'text' : type}
              value={value}
            />
            <button
              aria-label={visible ? `Hide ${name}` : `Show ${name}`}
              className="absolute right-0 top-1/2 -translate-y-1/2 rounded-full p-1 text-[var(--theme-text-muted)] transition hover:bg-[var(--theme-surface-soft)]"
              onClick={() => onToggleVisibility(name)}
              type="button"
            >
              {visible ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
            </button>
          </div>
        </div>
      </div>
    </label>
  );
}

export default ProfileSettingsModal;
